import { reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import LaisCrmConfigAPI from 'dashboard/api/laisCrmConfig';

/**
 * CRM integration of the agency: provider/endpoint/token configuration,
 * connectivity test and the "ready" activation gate.
 */
export function useMinhaLaisCrmConfig() {
  const isLoadingCrm = ref(false);
  const isSavingCrm = ref(false);
  const isTestingCrm = ref(false);
  const isActivatingCrm = ref(false);
  const crmProviderOptions = ref([]);
  const crmHelp = ref({});
  const crmConfig = reactive({
    provider: 'vista',
    provider_label: '',
    lead_endpoint_url: '',
    token: '',
    auth_header_name: 'Authorization',
    include_conversation: true,
    include_property: true,
    include_origin: true,
    status: 'draft',
    token_present: false,
    ready_for_test: false,
    last_test_status: null,
    last_test_error: null,
    last_test_payload: {},
    last_tested_at: null,
    activated_at: null,
  });

  const applyCrmPayload = payload => {
    const data = payload?.data || {};
    Object.assign(crmConfig, {
      provider: data.provider || 'vista',
      provider_label: data.provider_label || '',
      lead_endpoint_url: data.lead_endpoint_url || '',
      token: '',
      auth_header_name: data.auth_header_name || 'Authorization',
      include_conversation: data.metadata?.include_conversation !== false,
      include_property: data.metadata?.include_property !== false,
      include_origin: data.metadata?.include_origin !== false,
      status: data.status || 'draft',
      token_present: Boolean(data.token_present),
      ready_for_test: Boolean(data.ready_for_test),
      last_test_status: data.last_test_status || null,
      last_test_error: data.last_test_error || null,
      last_test_payload: data.last_test_payload || {},
      last_tested_at: data.last_tested_at || null,
      activated_at: data.activated_at || null,
    });
    crmProviderOptions.value =
      payload.provider_options || crmProviderOptions.value;
    crmHelp.value = payload.help || crmHelp.value;
  };

  const carregarCrmConfig = async () => {
    isLoadingCrm.value = true;
    try {
      const { data } = await LaisCrmConfigAPI.get();
      applyCrmPayload(data);
    } catch {
      useAlert('Não foi possível carregar a configuração de CRM.');
    } finally {
      isLoadingCrm.value = false;
    }
  };

  const crmPayload = () => ({
    provider: crmConfig.provider,
    provider_label: crmConfig.provider_label,
    lead_endpoint_url: crmConfig.lead_endpoint_url,
    token: crmConfig.token,
    auth_header_name: crmConfig.auth_header_name,
    include_conversation: crmConfig.include_conversation,
    include_property: crmConfig.include_property,
    include_origin: crmConfig.include_origin,
  });

  const salvarCrmConfig = async () => {
    isSavingCrm.value = true;
    try {
      const { data } = await LaisCrmConfigAPI.save(crmPayload());
      applyCrmPayload(data);
      useAlert('Configuração de CRM salva.');
    } catch (error) {
      useAlert(
        error.response?.data?.errors?.[0] || 'Não foi possível salvar o CRM.'
      );
    } finally {
      isSavingCrm.value = false;
    }
  };

  const testarCrmConfig = async () => {
    isTestingCrm.value = true;
    try {
      const { data } = await LaisCrmConfigAPI.test(crmPayload());
      applyCrmPayload(data);
      useAlert('Teste de CRM concluído com sucesso.');
    } catch (error) {
      if (error.response?.data?.data) applyCrmPayload(error.response.data);
      useAlert(
        error.response?.data?.data?.last_test_error ||
          error.response?.data?.errors?.[0] ||
          'O teste de CRM falhou.'
      );
    } finally {
      isTestingCrm.value = false;
    }
  };

  const ativarCrmConfig = async () => {
    isActivatingCrm.value = true;
    try {
      const { data } = await LaisCrmConfigAPI.activate();
      applyCrmPayload(data);
      useAlert('CRM marcado como pronto.');
    } catch (error) {
      useAlert(
        error.response?.data?.errors?.[0] || 'Não foi possível ativar o CRM.'
      );
    } finally {
      isActivatingCrm.value = false;
    }
  };

  return {
    crmConfig,
    crmProviderOptions,
    crmHelp,
    isLoadingCrm,
    isSavingCrm,
    isTestingCrm,
    isActivatingCrm,
    carregarCrmConfig,
    salvarCrmConfig,
    testarCrmConfig,
    ativarCrmConfig,
  };
}
